import React, { useState } from 'react';
import PageHeader from '../components/PageHeader';
import Card, { CardHeader } from '../components/Card';
import Button from '../components/Button';
import Toast, { ToastMessage } from '../components/Toast';
import { useAdminAuth } from '../context/AdminAuthContext';
import axios from 'axios';
import { BaseUrl } from '../services/adminApi';

export default function Settings() {
  const { admin } = useAdminAuth();
  const [fullName, setFullName] = useState(admin?.full_name || '');
  const [siteName, setSiteName] = useState('Manna Plag');
  const [supportEmail, setSupportEmail] = useState('support@mannaanalytics.com');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const showToast = (text: string, type: ToastMessage['type'] = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.patch(BaseUrl + 'auth/me/', { full_name: fullName }).catch(() => null);
      showToast('Profile updated.');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePlatform = async () => {
    setSaving(true);
    try {
      await axios.patch(BaseUrl + 'admin/settings/', {
        site_name: siteName,
        support_email: supportEmail,
        maintenance_mode: maintenanceMode,
      }).catch(() => null);
      showToast('Platform settings saved.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <Toast message={toast} />
      <PageHeader title="Settings" subtitle="Manage your admin profile and platform configuration." />

      <div className="flex flex-col gap-5">
        <Card>
          <CardHeader title="Admin Profile" subtitle="Your account details" />
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-[12px] font-bold text-gray-600 mb-1.5 block">Full name</label>
              <input
                value={fullName}
                onChange={e => setFullName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all"
              />
            </div>
            <div>
              <label className="text-[12px] font-bold text-gray-600 mb-1.5 block">Email address</label>
              <input
                value={admin?.email || ''}
                disabled
                className="w-full border border-gray-200 rounded-lg px-3 py-2.5 text-[13px] text-gray-500 bg-gray-50"
              />
            </div>
            <div className="flex justify-end mt-1">
              <Button size="sm" onClick={handleSaveProfile} loading={saving}>Save profile</Button>
            </div>
          </div>
        </Card>

        <Card>
          <CardHeader title="Platform Settings" subtitle="Global configuration for the site" />
          <div className="flex flex-col gap-3">
            <div>
              <label className="text-[12px] font-bold text-gray-600 mb-1.5 block">Site name</label>
              <input
                value={siteName}
                onChange={e => setSiteName(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all"
              />
            </div>
            <div>
              <label className="text-[12px] font-bold text-gray-600 mb-1.5 block">Support email</label>
              <input
                value={supportEmail}
                onChange={e => setSupportEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-[13px] text-gray-900 focus:outline-none focus:border-navy focus:ring-1 focus:ring-navy/20 transition-all"
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer mt-1">
              <input
                type="checkbox"
                checked={maintenanceMode}
                onChange={e => setMaintenanceMode(e.target.checked)}
                className="w-4 h-4 accent-navy"
              />
              <span className="text-[13px] font-semibold text-gray-700">Enable maintenance mode</span>
            </label>
            <div className="flex justify-end mt-1">
              <Button size="sm" onClick={handleSavePlatform} loading={saving}>Save settings</Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
